// src/services/auth.service.js
import { api, publicApi } from './api';
import firebasePhoneService from './firebasePhone.service';

class AuthService {
    // Demo users for local authentication
    demoUsers = {
        'admin@techcorp.com': {
            email: 'admin@techcorp.com',
            password: 'admin123',
            id: 'EMP001',
            name: 'Admin User',
            first_name: 'Admin',
            last_name: 'User',
            role: 'admin',
            role_name: 'HR Admin',
            is_admin: true,
            department: 'Human Resources',
            designation: 'HR Administrator',
            secondaryAccess: 'manager'
        }
    };

    async login(email, password, companyDomain) {
        console.log('🔐 LOGIN ATTEMPT - Email being sent:', email);
        console.log('🔐 LOGIN ATTEMPT - Email length:', email.length);
        console.log('🔐 LOGIN ATTEMPT - Email characters:', [...email].map(c => `${c}(${c.charCodeAt(0)})`).join(' '));

        // Try backend API first
        try {
            const response = await api.post('/api/auth/login', {
                email,
                password,
                companyDomain
            });

            //console.log('✅ Backend login successful:', response.data);

            if (response.data.success && response.data.token) {
                // Store auth data
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('company', JSON.stringify(response.data.company));

                return {
                    user: response.data.user,
                    company: response.data.company,
                    token: response.data.token
                };
            }
        } catch (error) {
            console.error('❌ Backend login failed:', error.response?.data || error.message);

            // Fallback to localStorage for backward compatibility
            //console.log('⚠️ Trying localStorage fallback...');

            // Check employees from localStorage (HR Portal employees)
            const storedEmployees = localStorage.getItem('hrportal_employees');
            if (storedEmployees) {
                try {
                    const employees = JSON.parse(storedEmployees);
                    const employee = employees.find(emp =>
                        emp.loginCredentials &&
                        emp.loginCredentials.loginId === email &&
                        emp.loginCredentials.password === password
                    );

                    if (employee) {
                        //console.log('✅ localStorage login success for:', employee.name);

                        const user = {
                            email: employee.email,
                            id: employee.id,
                            name: employee.name,
                            first_name: employee.first_name,
                            last_name: employee.last_name,
                            role: employee.accessLevel || 'employee',
                            role_name: this.getRoleName(employee.accessLevel),
                            is_admin: employee.accessLevel === 'admin',
                            is_manager: employee.accessLevel === 'manager',
                            department: employee.department,
                            designation: employee.designation,
                            employee_code: employee.employee_code,
                            secondaryAccess: employee.secondaryAccess || employee.secondary_access || 'employee'
                        };

                        const company = {
                            name: companyDomain || 'Your Company',
                            domain: companyDomain || 'company'
                        };

                        const token = 'local-token-' + Date.now();
                        localStorage.setItem('authToken', token);
                        localStorage.setItem('user', JSON.stringify(user));
                        localStorage.setItem('company', JSON.stringify(company));

                        return { user, company, token };
                    }
                } catch (parseError) {
                    console.error('Error parsing localStorage employees:', parseError);
                }
            }

            // Check demo users as last resort
            if (this.demoUsers[email] && this.demoUsers[email].password === password) {
                const user = this.demoUsers[email];
                const company = {
                    name: 'TechCorp',
                    domain: companyDomain || 'techcorp'
                };

                const token = 'demo-token-' + Date.now();
                localStorage.setItem('authToken', token);
                localStorage.setItem('user', JSON.stringify(user));
                localStorage.setItem('company', JSON.stringify(company));

                return { user, company, token };
            }

            throw error;
        }
    }

    getRoleName(accessLevel) {
        const roleMap = {
            'admin': 'HR Admin',
            'manager': 'Manager',
            'employee': 'Employee'
        };
        return roleMap[accessLevel] || accessLevel || 'Employee';
    }

    async register(userData) {
        const response = await api.post('/auth/register', userData);
        return response.data;
    }

    async logout() {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            localStorage.removeItem('company');
            window.location.href = '/login';
        }
    }

    async changePassword(currentPassword, newPassword) {
        const response = await api.post('/auth/change-password', {
            currentPassword,
            newPassword
        });
        return response.data;
    }

    async forgotPassword(email, companyDomain) {
        const response = await api.post('/api/auth/forgot-password', { email, companyDomain });
        return response.data;
    }

    async resetPassword(token, newPassword) {
        const response = await api.post('/api/auth/reset-password', { token, newPassword });
        return response.data;
    }

    getCurrentUser() {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    getCompany() {
        const companyStr = localStorage.getItem('company');
        return companyStr ? JSON.parse(companyStr) : null;
    }

    // ========== Organization Registration Verification Methods ==========

    // Check if email is already registered (duplicate check)
    async checkEmailDuplicate(email) {
        try {
            const response = await publicApi.post('/api/auth/check-email-duplicate', { email });
            return response.data;
        } catch (error) {
            // If backend not available, return exists: false for demo
            if (error.code === 'ERR_NETWORK' || error.response?.status === 404) {
                return { exists: false, demo: true };
            }
            throw error;
        }
    }

    // Check if phone is already registered (duplicate check)
    async checkPhoneDuplicate(phone) {
        const normalizedPhone = this.normalizePhone(phone);
        try {
            const response = await publicApi.post('/api/auth/check-phone-duplicate', { phone: normalizedPhone });
            return response.data;
        } catch (error) {
            // If backend not available, return exists: false for demo
            if (error.code === 'ERR_NETWORK' || error.response?.status === 404) {
                return { exists: false, demo: true };
            }
            throw error;
        }
    }

    // Send Email OTP for organization registration
    async sendEmailOTP(email) {
        console.log('📧 Sending Email OTP to:', email);

        try {
            const response = await publicApi.post('/api/auth/send-email-otp', { email });
            console.log('✅ Email OTP sent via backend');

            return {
                success: true,
                message: response.data.message || 'OTP sent to your email'
            };
        } catch (error) {
            console.error('❌ Backend Email OTP failed:', error.response?.data || error.message);
            throw new Error(error.response?.data?.error || 'Failed to send OTP to email. Please try again.');
        }
    }

    // Verify Email OTP for organization registration
    async verifyEmailOTP(email, otp) {
        console.log('🔐 Verifying Email OTP for:', email);

        try {
            await publicApi.post('/api/auth/verify-email-otp', { email, otp });
            console.log('✅ Email OTP verified via backend');
            return { success: true, verified: true };
        } catch (error) {
            console.error('❌ Backend Email OTP verification failed:', error.response?.data || error.message);
            throw new Error(error.response?.data?.error || 'Invalid OTP. Please try again.');
        }
    }

    // Send Phone OTP for organization registration
    async sendPhoneOTP(phone) {
        const normalizedPhone = this.normalizePhone(phone);
        console.log('📱 Sending Phone OTP for registration to:', normalizedPhone);

        // Store phone for org registration
        sessionStorage.setItem('org_reg_phone', normalizedPhone);

        // Production: Use Firebase Phone Auth
        if (this.isProduction()) {
            try {
                console.log('🔥 Sending OTP via Firebase Phone Auth...');
                const result = await firebasePhoneService.sendOTP(normalizedPhone);
                sessionStorage.setItem('org_otp_method', 'firebase');

                return {
                    success: true,
                    message: result.message || 'OTP sent to your mobile number',
                    demo: false
                };
            } catch (error) {
                console.error('❌ Firebase OTP failed:', error.message);
                throw new Error(error.message || 'Failed to send OTP. Please try again.');
            }
        } else {
            // Demo mode: Use backend or local fallback
            try {
                const response = await publicApi.post('/api/auth/send-phone-otp', { phone: normalizedPhone });
                sessionStorage.setItem('org_otp_method', 'backend');
                console.log('✅ Phone OTP sent successfully');

                return {
                    success: true,
                    message: response.data.message || 'OTP sent to your mobile number',
                    demo: true,
                    hint: response.data.hint
                };
            } catch (error) {
                console.error('❌ Backend Phone OTP failed:', error.response?.data || error.message);

                // Generate local demo OTP
                const demoOTP = Math.floor(100000 + Math.random() * 900000).toString();
                sessionStorage.setItem('org_phone_otp', demoOTP);
                sessionStorage.setItem('org_phone_otp_expiry', Date.now() + 5 * 60 * 1000);
                sessionStorage.setItem('org_otp_method', 'local');
                console.log(`🔐 Demo Phone OTP for ${normalizedPhone}: ${demoOTP}`);

                return {
                    success: true,
                    message: 'OTP sent successfully (Demo Mode)',
                    demo: true,
                    hint: `Demo OTP: ${demoOTP}`
                };
            }
        }
    }

    // Verify Phone OTP for organization registration
    async verifyPhoneOTP(phone, otp) {
        const normalizedPhone = this.normalizePhone(phone);
        console.log('🔐 Verifying Phone OTP for org registration:', normalizedPhone);

        const otpMethod = sessionStorage.getItem('org_otp_method') || 'local';

        // Firebase verification
        if (otpMethod === 'firebase') {
            try {
                const firebaseResult = await firebasePhoneService.verifyOTP(otp);
                firebasePhoneService.reset();
                sessionStorage.removeItem('org_otp_method');
                return { success: true, verified: true };
            } catch (error) {
                throw new Error(error.message || 'Invalid OTP. Please try again.');
            }
        }

        // Backend verification
        if (otpMethod === 'backend') {
            try {
                const response = await publicApi.post('/api/auth/verify-phone-otp', { phone: normalizedPhone, otp });
                sessionStorage.removeItem('org_otp_method');
                return { success: true, verified: true };
            } catch (error) {
                throw new Error(error.response?.data?.error || 'Invalid OTP. Please try again.');
            }
        }

        // Local verification (demo mode)
        if (otpMethod === 'local') {
            const storedOTP = sessionStorage.getItem('org_phone_otp');
            const otpExpiry = sessionStorage.getItem('org_phone_otp_expiry');

            if (!storedOTP || !otpExpiry) {
                throw new Error('OTP not found. Please request a new OTP.');
            }

            if (Date.now() > parseInt(otpExpiry)) {
                sessionStorage.removeItem('org_phone_otp');
                sessionStorage.removeItem('org_phone_otp_expiry');
                throw new Error('OTP has expired. Please request a new OTP.');
            }

            if (storedOTP !== otp) {
                throw new Error('Invalid OTP. Please try again.');
            }

            sessionStorage.removeItem('org_phone_otp');
            sessionStorage.removeItem('org_phone_otp_expiry');
            sessionStorage.removeItem('org_otp_method');
            console.log('✅ Phone OTP verified locally (demo mode)');
            return { success: true, verified: true, demo: true };
        }

        throw new Error('Invalid OTP verification method.');
    }

    // Register organization with verified data
    async registerOrganization(orgData) {
        console.log('📝 Registering organization:', orgData.companyName);

        try {
            const response = await publicApi.post('/api/auth/register-organization', {
                companyName: orgData.companyName,
                domain: orgData.domain,
                firstName: orgData.firstName,
                middleName: orgData.middleName || '',
                lastName: orgData.lastName,
                workEmail: orgData.workEmail,
                phone: this.normalizePhone(orgData.phone),
                teamSize: orgData.teamSize
            });

            return {
                success: true,
                message: response.data.message || 'Organization registered successfully',
                data: response.data
            };
        } catch (error) {
            console.error('❌ Organization registration failed:', error.response?.data || error.message);

            // Demo mode fallback for local testing without backend
            if (error.code === 'ERR_NETWORK' || error.response?.status === 401 || error.response?.status === 404) {
                console.log('🔧 Backend unavailable or requires auth. Using demo mode...');
                // Simulate successful registration
                await new Promise(resolve => setTimeout(resolve, 1500));
                return {
                    success: true,
                    message: 'Organization registered successfully (Demo Mode)',
                    demo: true,
                    data: {
                        organizationId: 'demo-org-' + Date.now(),
                        domain: 'portal.oak-hr.com',
                        adminEmail: orgData.workEmail
                    }
                };
            }

            throw new Error(error.response?.data?.error || 'Registration failed. Please try again.');
        }
    }

    // Complete organization registration - create user account and login
    async completeOrganizationRegistration(orgData) {
        console.log('🔐 Completing organization registration for:', orgData.workEmail);

        try {
            const response = await publicApi.post('/api/auth/complete-org-registration', {
                companyName: orgData.companyName,
                domain: orgData.domain,
                firstName: orgData.firstName,
                middleName: orgData.middleName || '',
                lastName: orgData.lastName,
                workEmail: orgData.workEmail,
                phone: this.normalizePhone(orgData.phone),
                teamSize: orgData.teamSize,
                password: orgData.password,
                accessLevel: orgData.accessLevel || 'admin',
                secondaryAccess: orgData.secondaryAccess || 'employee'
            });

            // Store auth data for auto-login
            if (response.data.token) {
                localStorage.setItem('authToken', response.data.token);
            }
            if (response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            if (response.data.company) {
                localStorage.setItem('company', JSON.stringify(response.data.company));
            }

            return {
                success: true,
                user: response.data.user,
                company: response.data.company,
                token: response.data.token
            };
        } catch (error) {
            console.error('❌ Complete registration failed:', error.response?.data || error.message);

            // Demo mode fallback
            if (error.code === 'ERR_NETWORK' || error.response?.status === 401 || error.response?.status === 404) {
                console.log('🔧 Backend unavailable, using demo mode for account creation...');

                // Create demo user account
                const demoUser = {
                    id: 'org-' + Date.now(),
                    email: orgData.workEmail,
                    name: `${orgData.firstName} ${orgData.lastName}`,
                    first_name: orgData.firstName,
                    middle_name: orgData.middleName || '',
                    last_name: orgData.lastName,
                    phone: this.normalizePhone(orgData.phone),
                    role: 'admin',
                    role_name: 'HR Admin',
                    is_admin: true,
                    is_manager: false,
                    accessLevel: 'admin',
                    secondaryAccess: 'employee',
                    employee_code: 'ADMIN001',
                    company_name: orgData.companyName,
                    company_domain: 'portal.oak-hr.com'
                };

                const demoCompany = {
                    id: 'comp-' + Date.now(),
                    name: orgData.companyName,
                    domain: 'portal.oak-hr.com'
                };

                const demoToken = 'org-token-' + Date.now();

                localStorage.setItem('authToken', demoToken);
                localStorage.setItem('user', JSON.stringify(demoUser));
                localStorage.setItem('company', JSON.stringify(demoCompany));

                console.log('✅ Demo account created:', demoUser);

                return {
                    success: true,
                    user: demoUser,
                    company: demoCompany,
                    token: demoToken,
                    demo: true
                };
            }

            throw new Error(error.response?.data?.error || 'Failed to create account. Please try again.');
        }
    }

    isAuthenticated() {
        return !!localStorage.getItem('authToken');
    }

    isAdmin() {
        const user = this.getCurrentUser();
        return user?.is_admin ||
               user?.role_name === 'Super Admin' ||
               user?.role_name === 'HR Admin' ||
               user?.access_level === 'admin';
    }

    isManager() {
        const user = this.getCurrentUser();
        return user?.is_manager ||
               user?.role === 'manager' ||
               user?.role_name === 'Manager' ||
               user?.access_level === 'manager';
    }

    // Check if running in production environment (Firebase OTP mode)
    isProduction() {
        // Use environment variable to control OTP mode explicitly
        // Set REACT_APP_OTP_MODE=firebase in Vercel to enable real SMS
        // Leave unset or set to 'demo' for demo mode (shows OTP hint)
        const otpMode = process.env.REACT_APP_OTP_MODE;
        const isFirebaseMode = otpMode === 'firebase';
        console.log('🔧 OTP Mode Check:', { otpMode, isFirebaseMode, env: process.env.NODE_ENV });
        return isFirebaseMode;
    }

    // OTP-based authentication methods
    async sendOTP(phone, companyDomain) {
        const normalizedPhone = this.normalizePhone(phone);
        console.log('📱 Sending OTP to:', normalizedPhone, 'Domain:', companyDomain);

        // Step 1: Verify phone exists in database
        let foundEmployee = null;

        try {
            const response = await api.post('/api/auth/verify-phone', {
                phone: normalizedPhone,
                companyDomain: companyDomain
            });

            if (response.data && response.data.success && response.data.employee) {
                foundEmployee = response.data.employee;
                console.log('✅ Employee found:', foundEmployee.first_name, foundEmployee.last_name);
            }
        } catch (error) {
            console.error('❌ Backend verify-phone failed:', error.response?.data || error.message);

            // If backend not available and in demo mode, use fallback
            if (!this.isProduction() && (error.response?.status === 404 || error.code === 'ERR_NETWORK')) {
                console.log('🔧 Backend not available, using demo fallback...');
                foundEmployee = await this.findEmployeeByPhoneAndDomain(normalizedPhone, companyDomain);
            } else {
                throw new Error(error.response?.data?.error || 'No employee found with this phone number for the specified company.');
            }
        }

        if (!foundEmployee) {
            throw new Error('No employee found with this phone number for the specified company.');
        }

        // Store found employee for later verification
        sessionStorage.setItem(`otp_employee_${normalizedPhone}`, JSON.stringify(foundEmployee));

        // Step 2: Send OTP
        // Production: Use Firebase Phone Auth (FREE 10K/month)
        // Demo: Use backend demo mode
        if (this.isProduction()) {
            try {
                console.log('🔥 Sending OTP via Firebase Phone Auth...');
                const result = await firebasePhoneService.sendOTP(normalizedPhone);
                sessionStorage.setItem(`otp_method_${normalizedPhone}`, 'firebase');

                return {
                    success: true,
                    message: result.message || 'OTP sent to your mobile number',
                    demo: false
                };
            } catch (error) {
                console.error('❌ Firebase OTP failed:', error.message);
                throw new Error(error.message || 'Failed to send OTP. Please try again.');
            }
        } else {
            // Demo mode: Use backend
            try {
                console.log('📤 Sending OTP via backend (demo mode)...');
                const response = await api.post('/api/auth/send-otp', {
                    phone: normalizedPhone,
                    companyDomain,
                    employeeId: foundEmployee.id
                });

                sessionStorage.setItem(`otp_method_${normalizedPhone}`, 'backend');
                console.log('✅ OTP sent successfully');

                return {
                    success: true,
                    message: response.data.message || 'OTP sent to your registered mobile number',
                    demo: true,
                    hint: response.data.hint
                };
            } catch (error) {
                console.error('❌ Backend OTP failed:', error.response?.data || error.message);

                // Generate local OTP if backend unavailable
                console.log('🔧 Backend unavailable, generating local demo OTP...');
                const demoOTP = Math.floor(100000 + Math.random() * 900000).toString();
                sessionStorage.setItem(`otp_${normalizedPhone}`, demoOTP);
                sessionStorage.setItem(`otp_expiry_${normalizedPhone}`, Date.now() + 5 * 60 * 1000);
                sessionStorage.setItem(`otp_method_${normalizedPhone}`, 'local');
                console.log(`🔐 Demo OTP for ${normalizedPhone}: ${demoOTP}`);

                return {
                    success: true,
                    message: 'OTP sent successfully (Demo Mode)',
                    demo: true,
                    hint: `Demo OTP: ${demoOTP}`
                };
            }
        }
    }

    async verifyOTP(phone, otp, companyDomain) {
        const normalizedPhone = this.normalizePhone(phone);
        console.log('🔐 Verifying OTP for:', normalizedPhone);

        // Get stored employee from sendOTP step
        const storedEmployeeStr = sessionStorage.getItem(`otp_employee_${normalizedPhone}`);
        if (!storedEmployeeStr) {
            throw new Error('Session expired. Please request a new OTP.');
        }
        const foundEmployee = JSON.parse(storedEmployeeStr);

        // Get OTP method used
        const otpMethod = sessionStorage.getItem(`otp_method_${normalizedPhone}`) || 'backend';
        console.log('OTP Method:', otpMethod);

        // Firebase verification (production)
        if (otpMethod === 'firebase') {
            try {
                console.log('🔥 Verifying OTP via Firebase...');
                const firebaseResult = await firebasePhoneService.verifyOTP(otp);

                // Firebase verified - now get JWT from our backend
                const response = await api.post('/api/auth/verify-firebase-otp', {
                    phone: normalizedPhone,
                    companyDomain,
                    firebaseIdToken: firebaseResult.idToken
                });

                // Clean up
                this.cleanupOTPSession(normalizedPhone);
                firebasePhoneService.reset();

                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user', JSON.stringify(response.data.user));
                localStorage.setItem('company', JSON.stringify(response.data.company));

                console.log('✅ Firebase OTP verified, user logged in:', response.data.user.first_name);
                return {
                    user: response.data.user,
                    company: response.data.company,
                    token: response.data.token
                };
            } catch (error) {
                console.error('❌ Firebase verification failed:', error.message);
                throw new Error(error.message || 'Invalid OTP. Please try again.');
            }
        }

        // Backend verification (demo mode with real backend)
        if (otpMethod === 'backend') {
            try {
                console.log('📤 Verifying OTP via backend...');
                const response = await api.post('/api/auth/verify-otp', {
                    phone: normalizedPhone,
                    otp,
                    companyDomain
                });

                if (response.data.success && response.data.token) {
                    this.cleanupOTPSession(normalizedPhone);

                    localStorage.setItem('authToken', response.data.token);
                    localStorage.setItem('user', JSON.stringify(response.data.user));
                    localStorage.setItem('company', JSON.stringify(response.data.company));

                    console.log('✅ OTP verified via backend, user logged in:', response.data.user.first_name);
                    return {
                        user: response.data.user,
                        company: response.data.company,
                        token: response.data.token
                    };
                }
                throw new Error('OTP verification failed');
            } catch (error) {
                console.error('❌ Backend OTP verification failed:', error.response?.data || error.message);
                throw new Error(error.response?.data?.error || 'Invalid OTP. Please try again.');
            }
        }

        // Local verification (demo mode without backend)
        if (otpMethod === 'local') {
            console.log('🔧 Verifying OTP locally...');

            const storedOTP = sessionStorage.getItem(`otp_${normalizedPhone}`);
            const otpExpiry = sessionStorage.getItem(`otp_expiry_${normalizedPhone}`);

            if (!storedOTP || !otpExpiry) {
                throw new Error('OTP not found. Please request a new OTP.');
            }

            if (Date.now() > parseInt(otpExpiry)) {
                this.cleanupOTPSession(normalizedPhone);
                throw new Error('OTP has expired. Please request a new OTP.');
            }

            if (storedOTP !== otp) {
                throw new Error('Invalid OTP. Please try again.');
            }

            // OTP verified - Clean up
            this.cleanupOTPSession(normalizedPhone);

            // Build user object from found employee
            const user = {
                email: foundEmployee.email,
                id: foundEmployee.id,
                name: foundEmployee.name || `${foundEmployee.first_name || ''} ${foundEmployee.last_name || ''}`.trim(),
                first_name: foundEmployee.first_name,
                last_name: foundEmployee.last_name,
                role: foundEmployee.role_name || 'employee',
                role_name: foundEmployee.role_name || 'Employee',
                is_admin: foundEmployee.is_admin === true,
                is_manager: foundEmployee.role_name === 'Manager',
                department: foundEmployee.department,
                designation: foundEmployee.designation,
                employee_code: foundEmployee.employee_code,
                phone: normalizedPhone
            };

            const company = {
                name: foundEmployee.company_name || companyDomain,
                domain: foundEmployee.company_domain || companyDomain
            };

            const token = 'otp-token-' + Date.now();
            localStorage.setItem('authToken', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('company', JSON.stringify(company));

            console.log('✅ OTP verified locally, user logged in:', user.name);
            return { user, company, token };
        }

        throw new Error('Invalid OTP verification method.');
    }

    // Helper to clean up OTP session data
    cleanupOTPSession(phone) {
        sessionStorage.removeItem(`otp_${phone}`);
        sessionStorage.removeItem(`otp_expiry_${phone}`);
        sessionStorage.removeItem(`otp_employee_${phone}`);
        sessionStorage.removeItem(`otp_method_${phone}`);
    }

    async resendOTP(phone, companyDomain) {
        // Clear previous OTP data
        const normalizedPhone = this.normalizePhone(phone);
        sessionStorage.removeItem(`otp_${normalizedPhone}`);
        sessionStorage.removeItem(`otp_expiry_${normalizedPhone}`);
        // Keep employee data to avoid re-validation

        // Resend OTP
        return this.sendOTP(phone, companyDomain);
    }

    normalizePhone(phone) {
        if (!phone) return '';
        // Remove all non-digit characters
        let normalized = phone.replace(/\D/g, '');

        // Remove country code if present (91 for India)
        if (normalized.length === 12 && normalized.startsWith('91')) {
            normalized = normalized.substring(2);
        }

        return normalized;
    }

    // Demo mode helper: Find employee by phone and domain from available sources
    async findEmployeeByPhoneAndDomain(phone, companyDomain) {
        console.log(`🔍 Demo mode: Searching for phone ${phone} in domain ${companyDomain}`);

        // Method 1: Try public endpoint for phone verification (no auth required)
        try {
            const response = await api.post('/api/auth/verify-phone', {
                phone: phone,
                companyDomain: companyDomain
            });
            if (response.data?.success && response.data?.employee) {
                console.log('✅ Employee found via public verify-phone:', response.data.employee.first_name);
                return response.data.employee;
            }
        } catch (error) {
            console.log('Public verify-phone not available:', error.message);
        }

        // Method 2: Try public employee lookup endpoint
        try {
            const response = await api.get('/api/public/employee-by-phone', {
                params: { phone, companyDomain }
            });
            if (response.data?.employee) {
                console.log('✅ Employee found via public lookup:', response.data.employee.first_name);
                return response.data.employee;
            }
        } catch (error) {
            console.log('Public employee lookup not available:', error.message);
        }

        // Method 3: Try localStorage (if user was previously logged in)
        try {
            const storedEmployees = localStorage.getItem('hrportal_employees');
            if (storedEmployees) {
                const employees = JSON.parse(storedEmployees);
                console.log(`📋 Found ${employees.length} employees from localStorage`);

                const found = employees.find(emp => {
                    const empPhone = emp.phone || emp.mobile || emp.contact_number;
                    if (!empPhone) return false;
                    return this.normalizePhone(empPhone) === phone;
                });

                if (found) {
                    console.log('✅ Employee found in localStorage:', found.first_name || found.name);
                    return found;
                }
            }
        } catch (error) {
            console.log('localStorage search failed:', error.message);
        }

        // Method 4: DEMO ONLY - Create a mock employee for testing
        // This allows testing OTP flow without backend
        console.log('⚠️ No backend available. Creating demo employee for testing...');
        console.log('💡 In production, backend must have POST /api/auth/verify-phone endpoint');

        const demoEmployee = {
            id: 'demo-' + Date.now(),
            first_name: 'Demo',
            last_name: 'User',
            name: 'Demo User',
            email: 'demo@' + companyDomain + '.com',
            phone: phone,
            department: 'Demo Department',
            designation: 'Demo Employee',
            accessLevel: 'employee',
            role: 'employee',
            employee_code: 'DEMO001',
            company_name: companyDomain
        };

        console.log('✅ Demo employee created for testing:', demoEmployee.name);
        return demoEmployee;
    }
}

export default new AuthService();
